import { useTranslation } from "react-i18next";
import { MetadataItem } from "./MetadataItem";

import type { Channel } from "@mirage/shared/search/search-result";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { useMemo } from "react";

export type DirectMessageMetadataProps = {
  channel: Channel;
  currentUserNames?: string[];
};

export const DirectMessageMetadata = ({
  channel,
  currentUserNames = [],
}: DirectMessageMetadataProps) => {
  const { t } = useTranslation();
  const { name } = channel;

  const displayName = useMemo(() => {
    const users = name.split(",").map((n) => n.trim());
    const filteredUsers = users.filter((n) => !currentUserNames.includes(n));

    if (users.length <= 1) {
      return "";
    }

    const [first, second, ...others] = filteredUsers;
    const count = filteredUsers.length;

    if (count === 0) {
      return "";
    }
    if (count === 1) {
      return t("dm_channel_one_person", {
        person: first,
      });
    }
    if (count === 2) {
      return t("dm_channel_two_people", {
        person1: first,
        person2: second,
      });
    }

    if (count === 3) {
      return t("dm_channel_three_people", {
        person1: first,
        person2: second,
        person3: others[0],
      });
    }

    return (
      <span>
        {`${first}, ${second}, `}
        <DigTooltip
          openDelay={250}
          title={others.map((name) => (
            <div key={name}>{name}</div>
          ))}
        >
          <span>
            {t("dm_channel_others", {
              count: others.length,
            })}
          </span>
        </DigTooltip>
      </span>
    );
  }, [currentUserNames, name]);

  return <MetadataItem title={displayName} />;
};
